/**
 * Payment Store - Zustand
 * State management for client payment tracking.
 *
 * Mirrors customerStore/userStore: the service owns I/O, the store owns the
 * in-memory list plus filter/sort/pagination derivation. Mutating actions
 * record errors and reset `isLoading` on failure (userStore pattern), then
 * re-throw so callers can surface a toast.
 */

import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import type {
  Payment,
  PaymentStats,
  PaymentFilters,
  CreatePaymentInput,
  UpdatePaymentInput,
  OnlinePaymentInput,
  RazorpayCheckoutOrder,
  RazorpayCheckoutProof,
} from '@/types/payment.types'
import { DEFAULT_PAYMENT_FILTERS } from '@/types/payment.types'
import { getPaymentService } from '@/services/payments'

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface PaymentState {
  // Data
  payments: Payment[]
  selectedPayment: Payment | null
  stats: PaymentStats | null
  isLoading: boolean
  isStatsLoading: boolean
  error: string | null

  // Filters
  filters: PaymentFilters
  searchQuery: string
  sortBy: 'createdAt' | 'amount' | 'status'
  sortOrder: 'asc' | 'desc'

  // Pagination
  page: number
  pageSize: number
}

interface PaymentActions {
  // Loading
  loadPayments: () => Promise<void>
  loadStats: () => Promise<void>

  // CRUD / lifecycle
  addPayment: (input: CreatePaymentInput) => Promise<Payment>
  startOnlinePayment: (input: OnlinePaymentInput, idempotencyKey: string) => Promise<RazorpayCheckoutOrder>
  verifyOnlinePayment: (proof: RazorpayCheckoutProof) => Promise<Payment>
  reconcilePayment: (id: string) => Promise<Payment>
  updateStatus: (id: string, updates: UpdatePaymentInput) => Promise<void>
  processPayment: (id: string, transactionId: string) => Promise<void>
  failPayment: (id: string, reason: string) => Promise<void>

  // Selection
  setSelectedPayment: (id: string | null) => void

  // Filters
  setFilters: (filters: Partial<PaymentFilters>) => void
  clearFilters: () => void
  setSearchQuery: (query: string) => void
  setSortBy: (sortBy: PaymentState['sortBy']) => void
  setSortOrder: (order: PaymentState['sortOrder']) => void

  // Pagination
  setPage: (page: number) => void
  setPageSize: (size: number) => void

  // Computed/Derived
  getFilteredPayments: () => Payment[]
  getPaymentById: (id: string) => Payment | undefined
  getPaginatedPayments: () => {
    items: Payment[]
    total: number
    page: number
    pageSize: number
    totalPages: number
  }

  // Reset
  resetStore: () => void
}

type PaymentStore = PaymentState & PaymentActions

// ============================================================================
// INITIAL STATE
// ============================================================================

const initialState: PaymentState = {
  payments: [],
  selectedPayment: null,
  stats: null,
  isLoading: false,
  isStatsLoading: false,
  error: null,
  filters: DEFAULT_PAYMENT_FILTERS,
  searchQuery: '',
  sortBy: 'createdAt',
  sortOrder: 'desc',
  page: 1,
  pageSize: 20,
}

/** Merge an updated payment into list + selected state. */
function mergePayment(
  state: PaymentState,
  payment: Payment
): Pick<PaymentState, 'payments' | 'selectedPayment' | 'isLoading'> {
  return {
    payments: state.payments.map((p) => (p.id === payment.id ? payment : p)),
    selectedPayment:
      state.selectedPayment?.id === payment.id ? payment : state.selectedPayment,
    isLoading: false,
  }
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const usePaymentStore = create<PaymentStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...initialState,

        // ========================================
        // Loading
        // ========================================

        loadPayments: async () => {
          set({ isLoading: true, error: null })
          try {
            const payments = await getPaymentService().list()
            set({ payments, isLoading: false })
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to load payments',
            })
          }
        },

        loadStats: async () => {
          set({ isStatsLoading: true })
          try {
            const stats = await getPaymentService().getStats()
            set({ stats, isStatsLoading: false })
          } catch (err) {
            set({
              isStatsLoading: false,
              error: err instanceof Error ? err.message : 'Failed to load payment stats',
            })
          }
        },

        // ========================================
        // CRUD / lifecycle
        // ========================================

        addPayment: async (input: CreatePaymentInput) => {
          set({ isLoading: true, error: null })
          try {
            const newPayment = await getPaymentService().create(input)
            set((state) => ({
              payments: [newPayment, ...state.payments],
              isLoading: false,
            }))
            return newPayment
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to record payment',
            })
            throw err
          }
        },

        startOnlinePayment: async (
          input: OnlinePaymentInput,
          idempotencyKey: string
        ) => {
          set({ isLoading: true, error: null })
          try {
            const order = await getPaymentService().createOnlineOrder(
              input,
              idempotencyKey
            )
            set((state) => ({
              payments: [
                order.payment,
                ...state.payments.filter((payment) => payment.id !== order.payment.id),
              ],
              isLoading: false,
            }))
            return order
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to start online payment',
            })
            throw err
          }
        },

        verifyOnlinePayment: async (proof: RazorpayCheckoutProof) => {
          set({ isLoading: true, error: null })
          try {
            const updated = await getPaymentService().verifyOnlinePayment(proof)
            set((state) => mergePayment(state, updated))
            return updated
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to verify online payment',
            })
            throw err
          }
        },

        reconcilePayment: async (id: string) => {
          set({ isLoading: true, error: null })
          try {
            const updated = await getPaymentService().reconcile(id)
            set((state) => mergePayment(state, updated))
            return updated
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to refresh provider status',
            })
            throw err
          }
        },

        updateStatus: async (id: string, updates: UpdatePaymentInput) => {
          set({ isLoading: true, error: null })
          try {
            const updated = await getPaymentService().updateStatus(id, updates)
            set((state) => mergePayment(state, updated))
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to update payment',
            })
            throw err
          }
        },

        processPayment: async (id: string, transactionId: string) => {
          set({ isLoading: true, error: null })
          try {
            const updated = await getPaymentService().process(id, transactionId)
            set((state) => mergePayment(state, updated))
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to process payment',
            })
            throw err
          }
        },

        failPayment: async (id: string, reason: string) => {
          set({ isLoading: true, error: null })
          try {
            const updated = await getPaymentService().fail(id, reason)
            set((state) => mergePayment(state, updated))
          } catch (err) {
            set({
              isLoading: false,
              error: err instanceof Error ? err.message : 'Failed to fail payment',
            })
            throw err
          }
        },

        // ========================================
        // Selection
        // ========================================

        setSelectedPayment: (id: string | null) => {
          const payment = id ? get().payments.find((p) => p.id === id) : null
          set({ selectedPayment: payment ?? null })
        },

        // ========================================
        // Filters
        // ========================================

        setFilters: (newFilters: Partial<PaymentFilters>) => {
          set((state) => ({
            filters: { ...state.filters, ...newFilters },
            page: 1,
          }))
        },

        clearFilters: () => {
          set({ filters: DEFAULT_PAYMENT_FILTERS, searchQuery: '', page: 1 })
        },

        setSearchQuery: (query: string) => {
          set({ searchQuery: query, page: 1 })
        },

        setSortBy: (sortBy: PaymentState['sortBy']) => {
          set({ sortBy })
        },

        setSortOrder: (order: PaymentState['sortOrder']) => {
          set({ sortOrder: order })
        },

        // ========================================
        // Pagination
        // ========================================

        setPage: (page: number) => {
          set({ page })
        },

        setPageSize: (pageSize: number) => {
          set({ pageSize, page: 1 })
        },

        // ========================================
        // Computed/Derived
        // ========================================

        getFilteredPayments: () => {
          const { payments, filters, searchQuery, sortBy, sortOrder } = get()

          let filtered = [...payments]

          if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim()
            filtered = filtered.filter(
              (p) =>
                (p.customerName?.toLowerCase().includes(query) ?? false) ||
                p.customerId.toLowerCase().includes(query) ||
                (p.transactionId?.toLowerCase().includes(query) ?? false) ||
                (p.description?.toLowerCase().includes(query) ?? false)
            )
          }

          if (filters.status !== 'all') {
            filtered = filtered.filter((p) => p.status === filters.status)
          }

          if (filters.method !== 'all') {
            filtered = filtered.filter((p) => p.paymentMethod === filters.method)
          }

          filtered.sort((a, b) => {
            let comparison = 0
            switch (sortBy) {
              case 'amount':
                comparison = a.amount - b.amount
                break
              case 'status':
                comparison = a.status.localeCompare(b.status)
                break
              case 'createdAt':
              default:
                comparison =
                  new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            }
            return sortOrder === 'asc' ? comparison : -comparison
          })

          return filtered
        },

        getPaymentById: (id: string) => {
          return get().payments.find((p) => p.id === id)
        },

        getPaginatedPayments: () => {
          const { page, pageSize } = get()
          const filtered = get().getFilteredPayments()
          const total = filtered.length
          const totalPages = Math.ceil(total / pageSize)
          const startIndex = (page - 1) * pageSize
          const items = filtered.slice(startIndex, startIndex + pageSize)

          return { items, total, page, pageSize, totalPages }
        },

        // ========================================
        // Reset
        // ========================================

        resetStore: () => {
          set(initialState)
        },
      }),
      {
        name: 'taxhive-payment-store',
        partialize: (state) => ({
          pageSize: state.pageSize,
          sortBy: state.sortBy,
          sortOrder: state.sortOrder,
        }),
      }
    ),
    { name: 'PaymentStore' }
  )
)

// ============================================================================
// SELECTORS
// ============================================================================

export const selectPayments = (state: PaymentStore) => state.payments
export const selectSelectedPayment = (state: PaymentStore) => state.selectedPayment
export const selectPaymentStats = (state: PaymentStore) => state.stats
export const selectIsLoading = (state: PaymentStore) => state.isLoading
export const selectError = (state: PaymentStore) => state.error
export const selectFilters = (state: PaymentStore) => state.filters
export const selectSearchQuery = (state: PaymentStore) => state.searchQuery
